// eslint-disable-next-line @typescript-eslint/no-explicit-any

import { Experience } from 'interface/experience/experience.interface';
import { Delivery } from 'store/templated-experience/templated-experience.type';
import { convertPeriodToSeconds as _convertPeriodToSeconds } from 'helpers/datetime.helper';

// Datetime utilities were moved to helpers/datetime.helper.ts. Re-exported
// here so existing callers that import from 'helpers/templated-experience.helper'
// keep working.
export {
  getTimezoneList,
  guessUserTimezone,
  getTimeRangeInMinutes,
  cleanupTimezone,
  convertDateToTimezone,
  convertTimezoneToDate,
  getDuration,
  getWarningOnLaunchScheduled,
  convertPeriodToSeconds,
  convertSecondsToPeriod,
  getMyTimezone,
} from 'helpers/datetime.helper';

export function getTenantId(): string {
  const tenantIdFromPathname = window.location.pathname.split('/')[2];
  const urlParams = new URLSearchParams(window.location.search);
  const tenantIdFromParams = urlParams.get('path')?.split('/')[1] || '';

  return tenantIdFromParams || tenantIdFromPathname;
}

/**
 * Returns the style for the experience label, based on the status property
 * @returns the style name
 * @param status
 */
export function defineStatusStyle(status: string) {
  if (status === 'active') return 'success';
  if (status === 'inactive') return 'error';
  if (status === 'draft') return 'default';
  if (status === 'scheduled') return 'info';
  return 'default';
}

/**
 * Returns updated instance with a new status
 * @returns updated instance object
 * @param instances
 * @param id
 * @param status
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateTemplatedInstanceStatus(instances: any, id: string, status: string) {
  return instances.data
    ? instances.data.map((instance: Experience) => {
        if (instance.id === id) {
          instance.status = status;
        }
        return instance;
      })
    : [];
}

/**
 * Returns serialized rule schedule data
 * @returns object
 */
export function serializeRuleSchedule(delivery: Delivery) {
  return {
    repeat: {
      limit: parseInt(delivery.frequency?.total) - 1 || 0,
      frequency: parseInt(delivery.frequency?.value) || 0,
      frequencyType: delivery.frequency?.period?.key || 'once',
    },
    modifier: _convertPeriodToSeconds(
      delivery.delay?.selected === 'SELECTED' && delivery.delay?.value ? delivery.delay.value : '',
      delivery.delay?.selected === 'SELECTED' && delivery.delay?.period?.key ? delivery.delay.period.key : '',
    ),
  };
}

/**
 * Returns serialized push delivery data, that match the backend requirements
 * @returns object
 */
export function serializeDelivery(delivery: Delivery) {
  const retVal = {
    max: parseInt(delivery.frequency?.total) || 0,
    cooldown: _convertPeriodToSeconds(delivery.frequency?.value || '', delivery.frequency?.period?.key || ''),
    limit: parseInt(delivery.limit?.key) || 0,
    remain: delivery.remain === 'SELECTED',
    delay: _convertPeriodToSeconds(
      delivery.delay?.selected === 'SELECTED' && delivery.delay?.value ? delivery.delay.value : '',
      delivery.delay?.selected === 'SELECTED' && delivery.delay?.period?.key ? delivery.delay.period.key : '',
    ),
  };

  return retVal;
}

/**
 * Returns the number with commas as thousands separators
 * @returns string
 */
export function numberWithCommas(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Returns default language
 * @returns string
 */
export function getDefaultLanguage(languages: { id: string; isDefault: boolean; name: string; direction?: string }[]) {
  return (
    languages?.find((lang) => {
      return lang?.isDefault;
    })?.id ?? 'en'
  );
}
